import { ProFormCheckbox } from '@ant-design/pro-components'
import type React from 'react'
import { EditableLabel } from '../components/EditableLabel'
import { EditableShell } from '../components/EditableShell'
import { useFieldContext } from '../createAppFormHook'
import { useEditableFieldMode } from '../hooks/useEditableFieldMode'
import { useMergedFieldProps } from '../utils/mergeFieldProps'

export type CheckboxFieldProps = React.ComponentProps<typeof ProFormCheckbox> & {
  /**
   * Invert the boolean value when reading/writing.
   * Useful when the stored value is `disabled` but the UI represents `enabled`.
   */
  inverse?: boolean
}

export function CheckboxField(props: CheckboxFieldProps) {
  const fieldCtx = useFieldContext<boolean>()
  const { inverse = false, formItemProps, fieldProps, proFieldProps, className, ...rest } = props

  const editable = useEditableFieldMode<boolean>()

  const merged = useMergedFieldProps({
    fieldCtx,
    editable,
    props: {
      ...props,
      layout: props.layout ?? 'horizontal',
    },
    fieldDefaults: {
      fieldProps: {
        checked:
          typeof fieldCtx.state.value === 'boolean'
            ? inverse
              ? !fieldCtx.state.value
              : fieldCtx.state.value
            : undefined,
        onChange: (e: unknown) => {
          const checked = (e as { target?: { checked?: boolean } }).target?.checked
          if (typeof checked === 'boolean') {
            fieldCtx.handleChange(inverse ? !checked : checked)
          }
        },
      },
    },
  })

  return (
    <EditableShell
      mode={editable.mode}
      enterEdit={editable.enterEdit}
      exitRead={editable.exitRead}
      className={className}
    >
      <ProFormCheckbox
        {...rest}
        // For checkbox set label to undefined and pass as children
        label={undefined}
        proFieldProps={merged.proFieldProps}
        formItemProps={merged.formItemProps}
        fieldProps={merged.fieldProps}
      >
        <EditableLabel
          mode={editable.mode}
          enterEdit={editable.enterEdit}
          exitRead={editable.exitRead}
          onUndo={editable.undoAndExit}
          canUndo={editable.canUndo}
        >
          {rest.label}
        </EditableLabel>
      </ProFormCheckbox>
    </EditableShell>
  )
}
